/**
 * The changes queued for the next revision, listed before they are applied.
 *
 * `apply_revisions` bumps the revision on every call, so qty/add/remove edits collect into ONE
 * batch (`lib/revisionBatch.ts`'s header). The cost of that batching is that a queued change
 * exists only in memory, and until now the only sign of it was a button reading "Apply 1 change"
 * — the requests table said "No revision requests recorded", because a purchaser's own edit is not
 * a customer request. So the purchaser could not see what they were about to send.
 *
 * Each row can be dropped on its own. "Discard changes" throws the whole batch away, which is the
 * wrong tool when four edits are right and the fifth was a mistake.
 */

import { Badge } from '../../components/Badge'
import { Button } from '../../components/ui'
import { formatMoney } from '../../lib/format'
import { describeEdit, type PendingEdit } from '../../lib/revisionBatch'
import type { Currency } from '../../lib/types'

const TONE: Record<PendingEdit['op'], 'ok' | 'warn' | 'danger'> = {
  add: 'ok',
  set_qty: 'warn',
  remove: 'danger',
}

export function PendingChangesCard({
  batch,
  nameByCode,
  currency,
  nextRevision,
  onDiscardOne,
}: {
  batch: PendingEdit[]
  /** `item_code` → catalogue name. A code alone is not something anyone can check. */
  nameByCode: Map<string, string>
  currency: Currency
  nextRevision: number
  onDiscardOne: (itemCode: string) => void
}) {
  if (batch.length === 0) return null

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="pad" style={{ borderBottom: '1px solid var(--line)' }}>
        <b>
          {batch.length} change{batch.length === 1 ? '' : 's'} queued
        </b>{' '}
        <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>
          — nothing is sent until you apply. They go out together as revision #{nextRevision}.
        </span>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th style={{ width: 120 }}>CHANGE</th>
            <th>ITEM</th>
            <th style={{ width: 140 }}>DETAIL</th>
            <th style={{ width: 130 }}>RECORDED COST</th>
            <th style={{ width: 90 }} />
          </tr>
        </thead>
        <tbody>
          {batch.map((edit) => {
            const { action, detail } = describeEdit(edit)
            return (
              <tr key={edit.item_code}>
                <td>
                  <Badge tone={TONE[edit.op]}>{action}</Badge>
                </td>
                <td>
                  {nameByCode.get(edit.item_code) || edit.item_code}
                  <div style={{ color: 'var(--muted)', fontSize: 11 }}>{edit.item_code}</div>
                </td>
                <td>{detail}</td>
                <td>
                  {/*
                    Shown because it is the thing most likely to be wrong and the hardest to spot
                    afterwards: a price recorded against the wrong supplier still applies silently.
                    An add with no recorded cost lands at zero, and says so rather than being blank.
                  */}
                  {edit.op !== 'add' ? (
                    <span style={{ color: 'var(--muted)' }}>—</span>
                  ) : edit.cost !== undefined && edit.cost > 0 ? (
                    formatMoney({ amount: edit.cost, currency })
                  ) : (
                    <span style={{ color: 'var(--warn)' }}>none — lands at 0.00</span>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <Button size="sm" onClick={() => onDiscardOne(edit.item_code)}>
                    Discard
                  </Button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
